// import { ComponentWithAs, IconProps } from '@chakra-ui/react'
import { JsonRpcProvider } from '@ethersproject/providers'
import type { AddEthereumChainParameter } from '@web3-react/types'
import { chainIDs } from 'config'

const ETH: AddEthereumChainParameter['nativeCurrency'] = {
  name: 'Ether',
  symbol: 'ETH',
  decimals: 18,
}

const MATIC: AddEthereumChainParameter['nativeCurrency'] = {
  name: 'Matic',
  symbol: 'MATIC',
  decimals: 18,
}

const BNB: AddEthereumChainParameter['nativeCurrency'] = {
  name: 'Binance Coin',
  symbol: 'BNB',
  decimals: 18,
}

const AVAX: AddEthereumChainParameter['nativeCurrency'] = {
  name: 'Avalanche',
  symbol: 'AVAX',
  decimals: 18,
}

interface BasicChainInformation {
  urls: string[]
  name: string
  icon: string
  testnet?: boolean
}

interface ExtendedChainInformation extends BasicChainInformation {
  nativeCurrency: AddEthereumChainParameter['nativeCurrency']
  blockExplorerUrls: AddEthereumChainParameter['blockExplorerUrls']
}

function isExtendedChainInformation(
  chainInformation: BasicChainInformation | ExtendedChainInformation
): chainInformation is ExtendedChainInformation {
  return !!(chainInformation as ExtendedChainInformation).nativeCurrency
}

export function getAddChainParameters(
  chainId: number
): AddEthereumChainParameter | number {
  const chainInformation = CHAINS[chainId]
  if (isExtendedChainInformation(chainInformation)) {
    return {
      chainId,
      chainName: chainInformation.name,
      nativeCurrency: chainInformation.nativeCurrency,
      rpcUrls: chainInformation.urls,
      blockExplorerUrls: chainInformation.blockExplorerUrls,
    }
  } else {
    return chainId
  }
}

export const CHAINS: { [chainId: number]: ExtendedChainInformation } = {
  // Ethereum
  1: {
    urls: ['https://rpc.ankr.com/eth/'],
    name: 'Ethereum',
    blockExplorerUrls: ['https://etherscan.io'],
    icon: '/static/icons/ethereum-icon.svg',
    nativeCurrency: ETH,
  },
  3: {
    urls: ['https://rpc.ankr.com/eth_ropsten/'],
    name: 'Ethereum (ropsten)',
    blockExplorerUrls: ['http://ropsten.etherscan.io'],
    icon: '/static/icons/ethereum-icon.svg',
    nativeCurrency: ETH,
    testnet: true,
  },
  // BSC
  56: {
    urls: ['https://bsc-dataseed.binance.org/'],
    name: 'BNB Chain',
    blockExplorerUrls: ['http://bscscan.com'],
    icon: '/static/icons/bsc-icon.svg',
    nativeCurrency: BNB,
  },
  // Polygon
  137: {
    urls: ['https://polygon-rpc.com/'],
    name: 'Polygon',
    nativeCurrency: MATIC,
    icon: '/static/icons/polygon-icon.svg',
    blockExplorerUrls: ['https://polygonscan.com'],
  },
  // Mumbai
  80001: {
    urls: ['https://matic-mumbai.chainstacklabs.com/'],
    name: 'Polygon (mumbai)',
    nativeCurrency: MATIC,
    icon: '/static/icons/polygon-icon.svg',
    blockExplorerUrls: ['https://mumbai.polygonscan.com'],
    testnet: true,
  },
  // Avalanche
  43114: {
    urls: ['https://api.avax.network/ext/bc/C/rpc'],
    name: 'Avalanche',
    nativeCurrency: AVAX,
    icon: '/static/icons/avalanche-icon.svg',
    blockExplorerUrls: ['https://snowtrace.io'],
  },
}

export function getChainName(chainId: number | undefined): string {
  if (chainId === undefined || chainId === -1) return ''
  return CHAINS[chainId]?.name
}
export function getChainSymbol(chainId: number | undefined): string {
  if (chainId === undefined || chainId === -1) return ''
  return CHAINS[chainId]?.nativeCurrency.symbol
}
export function getChainIcon(chainId: number | undefined): string {
  if (chainId === undefined || chainId === -1) return ''
  return CHAINS[chainId]?.icon
}
export function getChainPublicRPCProvider(chainId: number | undefined) {
  if (
    chainId === undefined ||
    chainId === -1 ||
    !CHAINS[chainId] ||
    !CHAINS[chainId].urls[0]
  )
    return undefined
  return new JsonRpcProvider(CHAINS[chainId].urls[0])
}

export const SUPPORTED_CHAINS: {
  [chainId: number]: BasicChainInformation | ExtendedChainInformation
} = Object.keys(CHAINS).reduce<{
  [chainId: number]: BasicChainInformation | ExtendedChainInformation
}>((accumulator, chainId) => {
  const supported: boolean = chainIDs.indexOf(Number(chainId)) > -1

  if (supported) {
    accumulator[Number(chainId)] = CHAINS[Number(chainId)]
  }

  return accumulator
}, {})

export const URLS: { [chainId: number]: string[] } = Object.keys(
  CHAINS
).reduce<{ [chainId: number]: string[] }>((accumulator, chainId) => {
  const validURLs: string[] = CHAINS[Number(chainId)].urls

  if (validURLs.length) {
    accumulator[Number(chainId)] = validURLs
  }

  return accumulator
}, {})
